import { useState } from "react";
import { useSWRConfig } from "swr";
import { approveJobPrice, createPriceApprovalQuote, type CostQuoteInfo, type JobInfo } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { CostConfirmationDialog } from "@/components/CostConfirmationDialog";
import { Button } from "@/components/ui/button";

export function PriceApprovalControl({ job }: { job: JobInfo }) {
  const userId = useAuthStore((state) => state.user?.id);
  const { mutate } = useSWRConfig();
  const [quote, setQuote] = useState<CostQuoteInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (job.state !== "awaiting_price_approval" || job.user_id !== userId) return null;

  async function prepare() {
    if (busy) return;
    setBusy(true); setError(null);
    try { setQuote(await createPriceApprovalQuote(job.id)); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Could not prepare the new price."); }
    finally { setBusy(false); }
  }
  async function confirm() {
    if (!quote || busy) return;
    setBusy(true); setError(null);
    try {
      await approveJobPrice(job.id, quote.id);
      setQuote(null);
      await mutate((key) => typeof key === "string" && (key.startsWith("/api/jobs") || key === "/api/wallet/balance"));
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Could not approve the price. Retry this quote."); }
    finally { setBusy(false); }
  }
  return (
    <div className="mt-2" onClick={(event) => event.stopPropagation()}>
      <p className="mb-2 text-xs text-studio-muted">Your previous reservation was released. Review the current price to continue this job.</p>
      <Button size="sm" disabled={busy} onClick={() => void prepare()}>Review new price</Button>
      {error && !quote && <p role="alert" className="mt-1 text-xs text-studio-danger">{error}</p>}
      <CostConfirmationDialog quote={quote} open={quote !== null} loading={busy} error={error}
        onClose={() => setQuote(null)} onConfirm={() => void confirm()} />
    </div>
  );
}
